import { createMetrics, formatTimeOnly } from "@context/shared";
import type { WritingMetrics } from "@context/shared";

/** One saved writing session, kept in the student's own browser. */
export interface DraftSession {
  /** Unique id, so entries stay stable while React maps them. */
  id: string;
  /** When the student clicked Save Draft. */
  savedAt: string;
  html: string;
  text: string;
  wordCount: number;
  /** The full evidence set captured at save time, so reloading restores it. */
  metrics: WritingMetrics;
  /** The assignment notes captured at save time, so reloading restores them. */
  referencePrompt: string;
}

/** LocalStorage key backing the saved-sessions dropdown. */
export const DRAFT_SESSIONS_STORAGE_KEY = "context.draftSessions";

/** Newest saves are kept; anything older is dropped when the cap is hit. */
export const MAX_DRAFT_SESSIONS = 10;

/**
 * Schema version of the stored payload. Version 1 wrote a bare array of
 * sessions with no version field; version 2 wraps the array as
 * `{ version, sessions }`. Keeping the constant lets future readers run
 * explicit per-version migrations instead of silently discarding sessions
 * whose shape has outgrown the current reader.
 */
export const DRAFT_SESSIONS_SCHEMA_VERSION = 2;

/** The unversioned bare-array shape older builds wrote. */
const LEGACY_UNVERSIONED_VERSION = 1;

interface StoredDraftSessions {
  version: number;
  sessions: DraftSession[];
}

function sortNewestFirst(sessions: DraftSession[]): DraftSession[] {
  return [...sessions].sort(
    (a, b) => new Date(b.savedAt).getTime() - new Date(a.savedAt).getTime(),
  );
}

function capSessions(sessions: DraftSession[]): DraftSession[] {
  return sortNewestFirst(sessions).slice(0, MAX_DRAFT_SESSIONS);
}

function isDraftSession(value: unknown): value is DraftSession {
  if (!value || typeof value !== "object") return false;
  const entry = value as Partial<DraftSession>;
  return (
    typeof entry.id === "string" &&
    typeof entry.savedAt === "string" &&
    typeof entry.html === "string" &&
    typeof entry.text === "string" &&
    typeof entry.wordCount === "number"
    // `metrics` and `referencePrompt` are optional here on purpose: entries
    // saved before they were snapshotted are still valid and are migrated to
    // an empty/zeroed value on load.
  );
}

/**
 * Entries saved before the prompt was snapshotted carry no `referencePrompt`
 * field. Give them an empty string so the type invariant holds downstream.
 */
function withMigratedPrompt(session: DraftSession): DraftSession {
  if (typeof session.referencePrompt === "string") return session;
  return { ...session, referencePrompt: "" };
}

/**
 * Entries saved before metrics were snapshotted carry no `metrics` field.
 * Give them a fresh zeroed set so the type invariant holds downstream; the
 * word count stays derived from the stored text, as the pre-metrics saves did.
 */
function withMigratedMetrics(session: DraftSession): DraftSession {
  if (session.metrics) return session;
  return { ...session, metrics: createMetrics(session.text, session.savedAt) };
}

function safeStorage(): Storage | undefined {
  try {
    return globalThis.localStorage;
  } catch {
    return undefined;
  }
}

/**
 * Runs every known stored shape through the per-version migrations, newest to
 * oldest, returning a session list. A payload written by a *newer* app version
 * is unreadable here and is bailed on rather than misparsed.
 */
function migrateStoredSessions(raw: unknown): DraftSession[] {
  let payload: { version: number; sessions: unknown };
  if (Array.isArray(raw)) {
    // Version 1: an unversioned bare array of sessions.
    payload = { version: LEGACY_UNVERSIONED_VERSION, sessions: raw };
  } else if (raw && typeof raw === "object" && "sessions" in raw) {
    const candidate = raw as { version?: unknown; sessions: unknown };
    payload = {
      version:
        typeof candidate.version === "number" ? candidate.version : LEGACY_UNVERSIONED_VERSION,
      sessions: candidate.sessions,
    };
  } else {
    return [];
  }

  if (payload.version > DRAFT_SESSIONS_SCHEMA_VERSION) return [];

  // Every known version shares the per-entry shape; the field-presence
  // migrations above cover the only differences (missing metrics / prompt).
  if (!Array.isArray(payload.sessions)) return [];
  return payload.sessions
    .filter(isDraftSession)
    .map(withMigratedMetrics)
    .map(withMigratedPrompt);
}

/**
 * Reads the saved sessions, tolerating corrupt storage (a failed parse or a
 * malformed entry just means that entry is skipped rather than crashing).
 */
export function loadSessions(storage: Storage | undefined = safeStorage()): DraftSession[] {
  try {
    const raw = storage?.getItem(DRAFT_SESSIONS_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    return capSessions(migrateStoredSessions(parsed));
  } catch {
    return [];
  }
}

/** A snapshot with the (potentially huge) HTML payload dropped. */
function toCompactSession(session: DraftSession): DraftSession {
  return { ...session, html: "" };
}

/** Writes the versioned payload; false when storage refuses the write. */
function writeSessions(sessions: DraftSession[], storage: Storage | undefined): boolean {
  try {
    if (sessions.length === 0) {
      storage?.removeItem(DRAFT_SESSIONS_STORAGE_KEY);
    } else {
      storage?.setItem(
        DRAFT_SESSIONS_STORAGE_KEY,
        JSON.stringify({
          version: DRAFT_SESSIONS_SCHEMA_VERSION,
          sessions,
        } satisfies StoredDraftSessions),
      );
    }
    return true;
  } catch {
    return false;
  }
}

/**
 * Persists one new session and returns the resulting list (capped). If storage
 * is full, the write degrades gracefully: first by dropping HTML payloads, then
 * by dropping the oldest session, and finally by keeping the list in memory and
 * reporting the failure through `onWarning`.
 */
export function saveSession(
  entry: DraftSession,
  storage: Storage | undefined = safeStorage(),
  onWarning?: (warning: string) => void,
): DraftSession[] {
  const sessions = capSessions([entry, ...loadSessions(storage)]);
  if (writeSessions(sessions, storage)) return sessions;

  // Quota is exhausted. A compact snapshot without the HTML still restores
  // everything that matters — text, metrics and the prompt.
  const compact = sessions.map(toCompactSession);
  if (writeSessions(compact, storage)) {
    onWarning?.("Storage is full, so saved sessions were kept without their formatting.");
    return compact;
  }

  // Still no room: drop the oldest session to make space for the new one.
  const trimmed = compact.slice(0, -1);
  if (trimmed.length > 0 && writeSessions(trimmed, storage)) {
    onWarning?.("Storage is full; the oldest saved session was removed to make room.");
    return trimmed;
  }

  onWarning?.("Storage is full, so this saved session could not be stored.");
  return sessions;
}

/** Removes a session and returns the resulting list. */
export function deleteSession(
  id: string,
  storage: Storage | undefined = safeStorage(),
): DraftSession[] {
  const sessions = loadSessions(storage).filter((session) => session.id !== id);
  writeSessions(sessions, storage);
  return sessions;
}

/** Removes every saved session from storage (used when the assignment is submitted). */
export function clearSessions(storage: Storage | undefined = safeStorage()): void {
  try {
    storage?.removeItem(DRAFT_SESSIONS_STORAGE_KEY);
  } catch {
    // Private-mode browsers can refuse writes; the in-memory list is cleared by the caller.
  }
}

/** Dropdown label, e.g. "Saved 2:34 PM · 123 words". */
export function formatSessionLabel(session: DraftSession): string {
  const wordCount = session.metrics?.wordCount ?? session.wordCount;
  return `Saved ${formatTimeOnly(session.savedAt)} · ${wordCount} words`;
}

/**
 * A globally unique session id. `Date.now()` ids collided across tabs (two tabs
 * could mint the same id and clobber each other's sessions); a UUID cannot.
 */
export function createSessionId(): string {
  return `sess_${crypto.randomUUID()}`;
}
